"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HttpBaseApi = void 0;
const HttpError_1 = require("./errors/HttpError");
const RequestError_1 = require("./errors/RequestError");
const fetchDefaults = {
    credentials: 'same-origin',
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
};
/**
 * Provides an interface that helps standardizing http requests from front-end
 */
class HttpBaseApi {
    /**
     * Make a network request of the specified type.
     * @param {string} method A string to set HTTP method for the request
     * @param {string} url The URL for the request
     * @param {object | null} body An object containing data that you want to apply to the request
     * @param {RequestOptions} options An object containing custom settings that you want to apply to the request
     * @return {Promise} A promise that resolves to the response given by the network request
     */
    static async method(method, url, body, options) {
        const requestInit = {
            ...fetchDefaults,
            headers: {
                ...fetchDefaults.headers,
                ...options?.headers,
            },
            method,
            signal: options?.signal || null,
            body: body ? JSON.stringify(body) : null,
        };
        let response;
        try {
            response = await fetch(url, requestInit);
        }
        catch (e) {
            if (e instanceof TypeError) {
                throw new RequestError_1.RequestError(e, url);
            }
            else {
                throw e;
            }
        }
        if (!response.ok) {
            let message = 'Server did not respond with a success status';
            let responseData;
            // get message from response if available
            try {
                responseData = await response.json();
                message = responseData?.message || message;
            }
            catch (err) {
                if (response.headers && response.headers.get('Content-Type')?.includes('application/json')) {
                    console.error(`HttpBaseApi: error parsing failed response as json. URL: ${url}, Error: `, err);
                }
            }
            throw new HttpError_1.HttpError(message, response.url, response.status, response.statusText, responseData);
        }
        else {
            let data;
            const meta = {
                status: response.status,
                statusText: response.statusText,
                options,
                requestHeaders: requestInit.headers || {},
                responseHeaders: response.headers,
            };
            // TODO: This try-catch is only temporary fix to stop errors.
            // This will be fixed properly once we have more clearity on the non-standard api responses
            try {
                data = await response.json();
            }
            catch (e) {
                data = response;
                meta.responseParseError = e;
            }
            return {
                data,
                _meta: meta,
            };
        }
    }
    /**
     * Make a network request of type 'GET'
     * @param {string} url The URL for the GET request
     * @param {RequestOptions} options An object containing custom settings that you want to apply to the request
     * @return {Promise} A promise that resolves to the response given by the network request
     */
    static get(url, options) {
        return HttpBaseApi.method('GET', url, null, options);
    }
    /**
     * Make a network request of type 'POST'
     * @param {string} url The URL for the POST request
     * @param {object} body An object containing data that you want to apply to the request
     * @param {RequestOptions} options An object containing custom settings that you want to apply to the request
     * @return {Promise} A promise that resolves to the response given by the network request
     */
    static post(url, body, options) {
        return HttpBaseApi.method('POST', url, body, options);
    }
    /**
     * Make a network request of type 'PUT'
     * @param {string} url The URL for the PUT request
     * @param {object} body An object containing data that you want to apply to the request
     * @param {RequestOptions} options An object containing custom settings that you want to apply to the request
     * @return {Promise} A promise that resolves to the response given by the network request
     */
    static put(url, body, options) {
        return HttpBaseApi.method('PUT', url, body, options);
    }
    /**
     * Make a network request of type 'PATCH'
     * @param {string} url The URL for the PATCH request
     * @param {object} body An object containing data that you want to apply to the request
     * @param {RequestOptions} options An object containing custom settings that you want to apply to the request
     * @return {Promise} A promise that resolves to the response given by the network request
     */
    static patch(url, body, options) {
        return HttpBaseApi.method('PATCH', url, body, options);
    }
    /**
     * Make a network request of type 'DELETE'
     * @param {string} url The URL for the DELETE request
     * @param {object} body An object containing data that you want to apply to the request
     * @param {RequestOptions} options An object containing custom settings that you want to apply to the request
     * @return {Promise} A promise that resolves to the response given by the network request
     */
    static delete(url, body, options) {
        return HttpBaseApi.method('DELETE', url, body, options);
    }
}
exports.HttpBaseApi = HttpBaseApi;
